import { cn } from "@faire/prototype-playground/lib/utils";

import type { Audience } from "../../../../pages/Ads2DControlsAlphaBeta/shared";
import { COPY } from "../copy";

const AD_TYPE_COPY = COPY.settings.adType;

/**
 * Matches the sibling settings modules, which take the same prop and shrink
 * their heading so it can sit under the larger "General settings" header.
 * Inlined rather than imported because the shared helper isn't exported.
 */
const headingClass = (level: "h2" | "h3") =>
  level === "h3"
    ? "text-[20px] leading-[28px] font-medium text-[#333]"
    : "text-[22px] leading-[32px] font-medium text-[#333]";

const OPTIONS: {
  value: Audience;
  title: string;
  description: string;
  tag?: string;
}[] = [
  { value: "both", ...AD_TYPE_COPY.both },
  { value: "new", ...AD_TYPE_COPY.new },
];

/**
 * This prototype's version of TargetAudienceModule
 * (pages/Ads2DControlsAlphaBeta/shared.tsx).
 *
 * Forked rather than edited in place because that module is rendered by five
 * other prototypes, which keep the audience framing. The state it edits is
 * unchanged — same `Audience` type, same two values — so nothing downstream of
 * the setting has to know which version is on screen; only the names the reader
 * sees are different, and they now match the Overview's ad type filter.
 *
 * The layout is the shared module's, deliberately: a fork that also drifts
 * visually stops being comparable to the prototypes it came from.
 */
export function AdTypeModule({
  audience,
  onAudienceChange,
  headingLevel: Heading = "h2",
}: {
  audience: Audience;
  onAudienceChange: (value: Audience) => void;
  headingLevel?: "h2" | "h3";
}) {
  return (
    <div className="bg-fs-surface-primary flex flex-col gap-6 rounded border border-[#dfe0e1] p-8">
      <div className="flex flex-col gap-1">
        <Heading className={headingClass(Heading)}>
          {AD_TYPE_COPY.heading}
        </Heading>
        <p className="max-w-[600px] text-sm text-[#333]">
          {AD_TYPE_COPY.subhead}
        </p>
      </div>
      <div className="flex gap-4">
        {OPTIONS.map((option) => {
          const selected = audience === option.value;
          return (
            <button
              key={option.value}
              onClick={() => onAudienceChange(option.value)}
              className={cn(
                "flex flex-1 cursor-pointer items-start gap-4 rounded border p-4 text-left transition-colors",
                selected
                  ? "border-(--slate-color-grey-1000)"
                  : "border-[#dfe0e1] hover:border-[#999]"
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">
                    <div
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-full border",
                        selected
                          ? "border-(--slate-color-grey-1000)"
                          : "border-[#333]"
                      )}
                    >
                      {selected && (
                        <div className="h-2 w-2 rounded-full bg-(--slate-color-grey-1000)" />
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* The tag sits at the card's edge rather than against the
                        title: the two titles are near-identical for their first
                        three words, so a tag trailing one of them reads as part
                        of the name. */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-[#333]">
                        {option.title}
                      </span>
                      {option.tag ? (
                        <span className="inline-flex shrink-0 items-center rounded border border-[#c5dcc5] bg-[#eaf3ea] px-2 py-0.5 text-[11px] leading-none font-medium text-[#2d4a38]">
                          {option.tag}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm leading-snug text-[#555]">
                      {option.description}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
